import { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEOHead from '../../components/feature/SEOHead';
import ExercicioGratisNavbar from '../exercicio-gratis/components/ExercicioGratisNavbar';
import CardCompartilhavel from './components/CardCompartilhavel';

// ─── Tipos ───────────────────────────────────────────────────────────────────
type PatternKey = 'sabotador' | 'perfeccionista' | 'controlador' | 'invisivel' | 'desconectado';

interface Question {
  id: number;
  text: string;
  options: { label: string; pattern: PatternKey }[];
}

interface Pattern {
  key: PatternKey;
  title: string;
  subtitle: string;
  description: string;
  insight: string;
  tecnicas: { nome: string; slug: string; icon: string; motivo: string }[];
  cor: string;
  icon: string;
}

// ─── Perguntas ────────────────────────────────────────────────────────────────
const questions: Question[] = [
  {
    id: 1,
    text: 'Quando você está prestes a dar um grande passo profissional — nova proposta, promoção, grande decisão — você tende a:',
    options: [
      { label: 'Encontrar bons motivos para adiar. Amanhã parece sempre melhor.', pattern: 'sabotador' },
      { label: 'Trabalhar até estar perfeito, mas dificilmente "lançar".', pattern: 'perfeccionista' },
      { label: 'Executar com excelência — mas chegar exausta do outro lado.', pattern: 'controlador' },
      { label: 'Entregar com qualidade, mas raramente receber o reconhecimento que merece.', pattern: 'invisivel' },
      { label: 'Questionar internamente se esse passo é realmente o que você quer.', pattern: 'desconectado' },
    ],
  },
  {
    id: 2,
    text: 'Em uma reunião de alto nível — board, C-suite, cliente VIP — você:',
    options: [
      { label: 'Tem a perspectiva certa, mas espera alguém com mais "autoridade" falar primeiro.', pattern: 'invisivel' },
      { label: 'Domina o assunto, mas se preocupa em não soar arrogante ou em excesso.', pattern: 'perfeccionista' },
      { label: 'Conduz tudo com presença, mas em casa sente um peso acumulado.', pattern: 'controlador' },
      { label: 'Está presente, mas uma voz interna questiona se você "merece" estar ali.', pattern: 'sabotador' },
      { label: 'Participa tecnicamente, mas algo te impede de conexão genuína com o grupo.', pattern: 'desconectado' },
    ],
  },
  {
    id: 3,
    text: 'Ao pensar nos próximos 3 a 5 anos da sua carreira ou negócio, o que você mais sente?',
    options: [
      { label: 'Clareza no destino, mas um bloqueio inexplicável no movimento.', pattern: 'sabotador' },
      { label: 'Um projeto ideal formulado na mente que nunca sai do papel.', pattern: 'perfeccionista' },
      { label: 'Cansaço só de imaginar a energia necessária para chegar lá.', pattern: 'controlador' },
      { label: 'Capacidade para mais, mas falta de visibilidade proporcional ao que você entrega.', pattern: 'invisivel' },
      { label: 'Incerteza sobre se este caminho é realmente o seu.', pattern: 'desconectado' },
    ],
  },
  {
    id: 4,
    text: 'Qual é sua maior frustração profissional neste momento?',
    options: [
      { label: 'Sei exatamente o que preciso fazer — e ainda assim não faço.', pattern: 'sabotador' },
      { label: 'Nunca está "bom o suficiente" para mim mesma, mesmo quando todos elogiam.', pattern: 'perfeccionista' },
      { label: 'Carrego responsabilidades que poderiam estar distribuídas, mas não consigo delegar.', pattern: 'controlador' },
      { label: 'Tenho impacto real, mas minha reputação ou salário não refletem isso.', pattern: 'invisivel' },
      { label: 'Tenho sucesso objetivo, mas algo essencial está ausente na minha vida.', pattern: 'desconectado' },
    ],
  },
  {
    id: 5,
    text: 'Em relacionamentos de alto nível — líderes, sócios, clientes premium — sua tendência é:',
    options: [
      { label: 'Diminuir o valor do que ofereço ao apresentar, mesmo sabendo que é excelente.', pattern: 'sabotador' },
      { label: 'Superentregar além do necessário por medo de desapontar quem confia em mim.', pattern: 'perfeccionista' },
      { label: 'Entregar resultados excepcionais, mas raramente pedir o que preciso em troca.', pattern: 'controlador' },
      { label: 'Criar valor consistente, mas não ser a primeira pessoa que vem à mente para oportunidades.', pattern: 'invisivel' },
      { label: 'Manter relações profissionais sólidas, mas raramente conexão verdadeiramente profunda.', pattern: 'desconectado' },
    ],
  },
];

// ─── Padrões ──────────────────────────────────────────────────────────────────
const patterns: Record<PatternKey, Pattern> = {
  sabotador: {
    key: 'sabotador',
    title: 'O Padrão Sabotador',
    subtitle: 'Alta competência. Baixa execução.',
    description: 'Você tem inteligência, visão e capacidade — mas um sistema interno de proteção intervém antes que você chegue no topo. O mecanismo que te salvou de riscos no passado está te impedindo de avançar no presente.',
    insight: 'A boa notícia: este é o padrão mais responsivo às técnicas de PNL. A transformação acontece ao nível da resposta automática — não da força de vontade.',
    tecnicas: [
      {
        nome: 'Técnica Swish',
        slug: 'tecnica-swish-pnl',
        icon: 'ri-refresh-line',
        motivo: 'Substitui o padrão de autossabotagem por uma resposta de avanço automático',
      },
      {
        nome: 'Ancoragem Emocional',
        slug: 'ancoragem-emocional',
        icon: 'ri-anchor-line',
        motivo: 'Cria um estado de confiança e ação acessível nos momentos críticos',
      },
    ],
    cor: 'from-rose-500 to-rose-700',
    icon: 'ri-shield-flash-line',
  },
  perfeccionista: {
    key: 'perfeccionista',
    title: 'O Padrão Perfeccionista',
    subtitle: 'Excelência paralisante.',
    description: 'Sua capacidade de enxergar o que pode melhorar é extraordinária — e ao mesmo tempo o maior obstáculo. O "ainda não está bom" que você aplica a tudo te protegeu de julgamentos no passado, mas hoje opera como travão de lançamento.',
    insight: 'Não é falta de esforço. É um mecanismo de autopreservação sofisticado. A PNL trabalha diretamente nesta estrutura interna.',
    tecnicas: [
      {
        nome: 'Ressignificação (Reframing)',
        slug: 'ressignificacao-reframing-pnl',
        icon: 'ri-eye-line',
        motivo: 'Transforma o padrão "perfeito ou não" em "melhor possível agora"',
      },
      {
        nome: 'Linha do Tempo PNL',
        slug: 'linha-tempo-pnl',
        icon: 'ri-time-line',
        motivo: 'Libera as experiências que criaram a necessidade de perfeição',
      },
    ],
    cor: 'from-amber-500 to-amber-700',
    icon: 'ri-scales-3-line',
  },
  controlador: {
    key: 'controlador',
    title: 'O Padrão Controlador',
    subtitle: 'Resultado alto. Custo pessoal altíssimo.',
    description: 'Você é extremamente eficaz — e paga um preço enorme por isso. A dificuldade em delegar e confiar em outros não é falta de liderança: é uma crença profunda de que "só você consegue garantir o padrão que importa".',
    insight: 'Este padrão se desenvolveu como resposta a uma necessidade real de controle em algum momento. Hoje, ele limita seu escalamento e drena sua energia vital.',
    tecnicas: [
      {
        nome: 'Submodalidades PNL',
        slug: 'submodalidades-pnl',
        icon: 'ri-settings-3-line',
        motivo: 'Ajusta a intensidade das crenças de controle e reduz a carga emocional',
      },
      {
        nome: 'Ancoragem Emocional',
        slug: 'ancoragem-emocional',
        icon: 'ri-anchor-line',
        motivo: 'Instala um estado de confiança e soltura acessível por demanda',
      },
    ],
    cor: 'from-orange-500 to-orange-700',
    icon: 'ri-settings-5-line',
  },
  invisivel: {
    key: 'invisivel',
    title: 'O Padrão Invisível de Sucesso',
    subtitle: 'Impacto real. Visibilidade desproporcional.',
    description: 'Sua entrega é consistentemente excelente — mas sua presença e posicionamento não refletem esse valor. Não é falta de talento nem de resultados. É uma identidade interna que ainda não acompanhou o nível externo que você atingiu.',
    insight: 'Você provavelmente aprendeu que ocupar espaço com poder era perigoso ou inadequado. A PNL trabalha direto nesta identidade para alinhá-la ao seu desempenho real.',
    tecnicas: [
      {
        nome: 'Identidade de Alta Performance',
        slug: 'identidade-alta-performance',
        icon: 'ri-user-star-line',
        motivo: 'Reconstrói a identidade interna para corresponder ao nível de impacto real',
      },
      {
        nome: 'Modelagem de Excelência',
        slug: 'modelagem-excelencia-pnl',
        icon: 'ri-star-line',
        motivo: 'Replica a estrutura interna de quem já ocupa o espaço com naturalidade',
      },
    ],
    cor: 'from-emerald-600 to-emerald-800',
    icon: 'ri-user-star-line',
  },
  desconectado: {
    key: 'desconectado',
    title: 'O Padrão Desconectado',
    subtitle: 'Sucesso objetivo. Vazio interno.',
    description: 'Você construiu uma carreira sólida — e algo essencial ainda está ausente. Este padrão não é sobre fracasso. É sobre ter seguido um caminho que faz sentido para todos ao redor, mas que ressoa cada vez menos em você.',
    insight: 'A desconexão entre conquistas externas e realização interna é um dos padrões mais prevalentes em mulheres de alta performance. A PNL trabalha com propósito e valores no nível mais profundo.',
    tecnicas: [
      {
        nome: 'Propósito de Vida',
        slug: 'proposito-vida',
        icon: 'ri-compass-3-line',
        motivo: 'Reconecta com os valores e missão que realmente movem você',
      },
      {
        nome: 'Modelagem de Excelência',
        slug: 'modelagem-excelencia-pnl',
        icon: 'ri-star-line',
        motivo: 'Mapeia padrões de realização genuína para replicar internamente',
      },
    ],
    cor: 'from-indigo-600 to-indigo-800',
    icon: 'ri-compass-discover-line',
  },
};

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const diagnosticoSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Diagnóstico de Padrão Mental | Método Bia Holmes',
    url: `${SITE_URL}/diagnostico`,
    inLanguage: 'pt-BR',
    description: 'Quiz gratuito de 5 perguntas que identifica o padrão mental dominante que está freando seu próximo nível.',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Início', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Diagnóstico', item: `${SITE_URL}/diagnostico` },
      ],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Quiz',
    name: 'Diagnóstico de Padrão Mental — Bia Holmes',
    description: 'Descubra em 5 perguntas qual padrão mental está travando seu próximo nível e receba a técnica de PNL exata para superá-lo.',
    about: { '@type': 'Thing', name: 'Reprogramação Mental e PNL' },
    educationalLevel: 'Avançado',
    inLanguage: 'pt-BR',
    isAccessibleForFree: true,
    numberOfQuestions: 5,
    author: {
      '@type': 'Person',
      name: 'Bia Holmes',
      url: `${SITE_URL}/sobre`,
      jobTitle: 'Especialista em PNL Clínica',
    },
    educationalAlignment: {
      '@type': 'AlignmentObject',
      educationalFramework: 'Programação Neurolinguística (PNL)',
      targetName: 'Identificação de padrões mentais limitantes',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'O que é o Padrão Sabotador?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Alta competência com baixa execução. Um sistema interno de proteção que intervém antes de você chegar ao topo.',
        },
      },
      {
        '@type': 'Question',
        name: 'O que é o Padrão Perfeccionista?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Excelência paralisante. A capacidade de sempre enxergar o que pode melhorar acaba impedindo o lançamento.',
        },
      },
      {
        '@type': 'Question',
        name: 'O que é o Padrão Controlador?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Resultado alto com custo pessoal altíssimo. Dificuldade em delegar por crença de que só você garante o padrão de qualidade.',
        },
      },
      {
        '@type': 'Question',
        name: 'O que é o Padrão Invisível de Sucesso?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Impacto real com visibilidade desproporcional. A identidade interna ainda não acompanhou o nível externo atingido.',
        },
      },
      {
        '@type': 'Question',
        name: 'O que é o Padrão Desconectado?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Sucesso objetivo com vazio interno. Conquistas externas sólidas mas algo essencial ainda está ausente.',
        },
      },
    ],
  },
];

// ─── Componente ───────────────────────────────────────────────────────────────
export default function DiagnosticoPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'intro' | 'quiz' | 'result' | 'capture'>('intro');
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState<PatternKey[]>([]);
  const [result, setResult] = useState<Pattern | null>(null);
  const [leadName, setLeadName] = useState('');
  const [leadEmail, setLeadEmail] = useState('');
  const [leadLoading, setLeadLoading] = useState(false);
  const [leadSent, setLeadSent] = useState(false);

  const handleAnswer = (pattern: PatternKey) => {
    const newAnswers = [...answers, pattern];
    if (currentQ < questions.length - 1) {
      setAnswers(newAnswers);
      setCurrentQ(currentQ + 1);
    } else {
      // Calcula o padrão dominante
      const counts: Record<PatternKey, number> = {
        sabotador: 0, perfeccionista: 0, controlador: 0, invisivel: 0, desconectado: 0,
      };
      newAnswers.forEach((p) => counts[p]++);
      const dominant = (Object.keys(counts) as PatternKey[]).reduce(
        (a, b) => (counts[a] >= counts[b] ? a : b)
      );
      setResult(patterns[dominant]);
      setAnswers(newAnswers);
      setStep('result');
    }
  };

  const handleLeadSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLeadLoading(true);
    const data = new URLSearchParams();
    data.append('name', leadName);
    data.append('email', leadEmail);
    data.append('diagnostico', result?.title ?? '');
    try {
      await fetch('https://readdy.ai/api/form/d6tdqcdm9vk3c28i5h8g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setLeadSent(true);
    } catch {
      setLeadSent(true);
    } finally {
      setLeadLoading(false);
    }
  };

  const progress = step === 'quiz' ? ((currentQ + 1) / questions.length) * 100 : 0;

  return (
    <div className="min-h-screen bg-cream-50">
      <SEOHead
        title="Diagnóstico de Padrão Mental | Método Bia Holmes"
        description="Quiz gratuito de 5 perguntas que identifica o padrão mental dominante que está freando seu próximo nível — e recomenda a técnica de PNL exata para superá-lo."
        keywords="diagnóstico mental PNL, padrão mental limitante, quiz PNL, Bia Holmes, reprogramação mental executivas"
        canonical="/diagnostico"
        schema={diagnosticoSchema}
      />

      <ExercicioGratisNavbar />

      {/* ─── INTRO ─── */}
      {step === 'intro' && (
        <section className="relative min-h-screen flex items-center justify-center px-6 pt-24 pb-16 bg-gradient-to-br from-charcoal-950 via-charcoal-900 to-charcoal-950 overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] bg-gold-500/5 rounded-full blur-3xl"></div>
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_40%_30%,_rgba(201,164,64,0.06)_0%,_transparent_60%)]"></div>
          </div>

          <div className="max-w-3xl mx-auto relative z-10 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold-500/10 border border-gold-500/20 rounded-full text-gold-400 text-xs font-semibold tracking-widest uppercase mb-8 font-['Montserrat']">
              <i className="ri-brain-line"></i>
              Diagnóstico gratuito · 5 minutos
            </div>

            <h1 className="font-display text-5xl md:text-6xl font-bold text-white leading-tight mb-6">
              Qual padrão mental está<br />
              <span className="text-gold-400">travando seu próximo nível?</span>
            </h1>

            <p className="text-white/60 text-lg font-light leading-relaxed font-['Montserrat'] mb-10 max-w-2xl mx-auto">
              5 perguntas. Resultado personalizado. A técnica de PNL exata para o seu perfil — baseado em mais de 15 anos de prática clínica com executivas e líderes.
            </p>

            {/* O que você vai descobrir */}
            <div className="grid sm:grid-cols-3 gap-4 mb-12">
              {[
                { icon: 'ri-search-eye-line', text: 'Seu padrão mental dominante' },
                { icon: 'ri-tools-line', text: 'As técnicas exatas para você' },
                { icon: 'ri-road-map-line', text: 'Plano de ação personalizado' },
              ].map((item) => (
                <div key={item.text} className="bg-white/5 border border-gold-500/10 rounded-xl px-5 py-4 text-center">
                  <i className={`${item.icon} text-2xl text-gold-400 mb-2 block`}></i>
                  <p className="text-white/60 text-xs font-['Montserrat'] leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>

            {/* 5 padrões preview */}
            <div className="flex flex-wrap justify-center gap-2 mb-12">
              {Object.values(patterns).map((p) => (
                <div key={p.key} className="flex items-center gap-2 px-3 py-1.5 bg-white/5 border border-white/8 rounded-full">
                  <i className={`${p.icon} text-gold-400/70 text-xs`}></i>
                  <span className="text-white/40 text-xs font-['Montserrat']">{p.title.replace('O Padrão ', '')}</span>
                </div>
              ))}
            </div>

            <button
              onClick={() => setStep('quiz')}
              className="inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-gold-500 to-gold-700 text-charcoal-900 rounded-full font-bold text-base hover:from-gold-600 hover:to-gold-800 transition-all whitespace-nowrap font-['Montserrat'] tracking-wide cursor-pointer"
            >
              <i className="ri-play-circle-line text-lg"></i>
              Iniciar Diagnóstico
            </button>

            <p className="text-white/25 text-xs mt-6 font-['Montserrat']">
              Gratuito · Sem cadastro para começar · Resultado em menos de 5 minutos
            </p>
          </div>
        </section>
      )}

      {/* ─── QUIZ ─── */}
      {step === 'quiz' && (
        <section className="min-h-screen flex items-center justify-center px-6 pt-28 pb-16">
          <div className="max-w-2xl w-full mx-auto">
            {/* Progress */}
            <div className="mb-8">
              <div className="flex justify-between text-xs text-charcoal-400 font-['Montserrat'] mb-2">
                <span>Pergunta {currentQ + 1} de {questions.length}</span>
                <span>{Math.round(progress)}% concluído</span>
              </div>
              <div className="h-1.5 bg-charcoal-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-gold-500 to-gold-700 rounded-full transition-all duration-500"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>

            {/* Pergunta */}
            <div className="bg-white border border-charcoal-100 rounded-2xl p-8 mb-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gold-50 border border-gold-200 rounded-full text-gold-700 text-xs font-semibold tracking-widest uppercase mb-6 font-['Montserrat']">
                <i className="ri-question-line"></i>
                Pergunta {currentQ + 1}
              </div>

              <h2 className="font-display text-xl font-bold text-charcoal-900 leading-relaxed mb-8">
                {questions[currentQ].text}
              </h2>

              <div className="space-y-3">
                {questions[currentQ].options.map((opt, i) => (
                  <button
                    key={i}
                    onClick={() => handleAnswer(opt.pattern)}
                    className="w-full text-left px-5 py-4 bg-cream-50 border border-charcoal-100 rounded-xl hover:border-gold-300 hover:bg-gold-50 transition-all duration-200 group cursor-pointer"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-7 h-7 flex items-center justify-center rounded-full border border-charcoal-200 group-hover:border-gold-400 group-hover:bg-gold-100 transition-all flex-shrink-0 mt-0.5">
                        <span className="text-xs font-bold text-charcoal-400 group-hover:text-gold-600 font-['Montserrat']">
                          {String.fromCharCode(65 + i)}
                        </span>
                      </div>
                      <p className="text-sm text-charcoal-700 font-['Montserrat'] leading-relaxed group-hover:text-charcoal-900 transition-colors">
                        {opt.label}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Voltar */}
            {currentQ > 0 && (
              <button
                onClick={() => { setCurrentQ(currentQ - 1); setAnswers(answers.slice(0, -1)); }}
                className="flex items-center gap-2 text-charcoal-400 hover:text-charcoal-600 text-sm font-['Montserrat'] transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                Voltar
              </button>
            )}
          </div>
        </section>
      )}

      {/* ─── RESULTADO ─── */}
      {step === 'result' && result && (
        <div className="pt-24 pb-16">
          {/* Hero resultado */}
          <section className="px-6 py-16 bg-gradient-to-br from-charcoal-950 via-charcoal-900 to-charcoal-950 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(201,164,64,0.07)_0%,_transparent_60%)] pointer-events-none"></div>
            <div className="max-w-3xl mx-auto relative z-10">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gold-500/15 border border-gold-500/25 mx-auto mb-6">
                <i className={`${result.icon} text-3xl text-gold-400`}></i>
              </div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold-500/10 border border-gold-500/20 rounded-full text-gold-400 text-xs font-semibold tracking-widest uppercase mb-6 font-['Montserrat']">
                <i className="ri-brain-line"></i>
                Seu padrão dominante
              </div>
              <h1 className="font-display text-5xl font-bold text-white mb-3">{result.title}</h1>
              <p className="text-gold-400 text-lg font-semibold font-['Montserrat'] mb-8">{result.subtitle}</p>
            </div>
          </section>

          <div className="max-w-4xl mx-auto px-6">
            {/* Análise */}
            <div className="bg-white border border-charcoal-100 rounded-2xl p-8 my-8">
              <h2 className="font-display text-2xl font-bold text-charcoal-900 mb-4">O que isso significa para você</h2>
              <p className="text-charcoal-700 font-['Montserrat'] leading-relaxed text-base mb-5">{result.description}</p>
              <div className="bg-gold-50 border border-gold-200 rounded-xl px-5 py-4">
                <div className="flex items-start gap-3">
                  <i className="ri-lightbulb-line text-gold-500 text-lg mt-0.5 flex-shrink-0"></i>
                  <p className="text-charcoal-700 text-sm font-['Montserrat'] leading-relaxed">{result.insight}</p>
                </div>
              </div>
            </div>

            {/* Técnicas recomendadas */}
            <div className="mb-8">
              <h2 className="font-display text-2xl font-bold text-charcoal-900 mb-6">
                Técnicas recomendadas para o seu padrão
              </h2>
              <div className="grid sm:grid-cols-2 gap-5">
                {result.tecnicas.map((t) => (
                  <div key={t.slug} className="bg-white border border-charcoal-100 rounded-2xl p-6 hover:border-gold-300 transition-all group">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-gold-50 border border-gold-100 mb-4 group-hover:bg-gold-100 transition-all">
                      <i className={`${t.icon} text-2xl text-gold-600`}></i>
                    </div>
                    <h3 className="font-display text-lg font-bold text-charcoal-800 mb-2">{t.nome}</h3>
                    <p className="text-sm text-charcoal-500 font-['Montserrat'] font-light leading-relaxed mb-4">{t.motivo}</p>
                    <Link
                      to={`/exercicio-gratis/${t.slug}`}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-gold-600 hover:text-gold-700 transition-colors font-['Montserrat']"
                    >
                      Praticar agora — versão gratuita
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                ))}
              </div>
            </div>

            {/* Card compartilhável Instagram */}
            <CardCompartilhavel
              titulo={result.title}
              subtitulo={result.subtitle}
              insight={result.insight}
              icon={result.icon}
              cor={result.cor}
            />

            {/* Lead capture pós-resultado */}
            {!leadSent ? (
              <div className="bg-gradient-to-br from-charcoal-900 to-charcoal-950 rounded-2xl p-8 border border-gold-500/10">
                <div className="max-w-lg mx-auto text-center">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gold-500/10 border border-gold-500/20 rounded-full text-gold-400 text-xs font-semibold tracking-widest uppercase mb-5 font-['Montserrat']">
                    <i className="ri-mail-send-line"></i>
                    Resultado + plano personalizado
                  </div>
                  <h3 className="font-display text-2xl font-bold text-white mb-3 leading-tight">
                    Receba seu diagnóstico completo<br />
                    <span className="text-gold-400">por e-mail</span>
                  </h3>
                  <p className="text-white/50 text-sm font-['Montserrat'] font-light mb-7 leading-relaxed">
                    Incluindo o protocolo de 7 dias para começar a dissolver o <strong className="text-white/70">{result.title.replace('O Padrão ', '').toLowerCase()}</strong> e as técnicas de PNL exatas para o seu perfil.
                  </p>

                  <form
                    data-readdy-form
                    onSubmit={handleLeadSubmit}
                    className="space-y-3"
                  >
                    <input
                      name="name"
                      type="text"
                      required
                      value={leadName}
                      onChange={(e) => setLeadName(e.target.value)}
                      placeholder="Seu nome"
                      className="w-full px-4 py-3.5 bg-white/8 border border-white/15 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-gold-400/60 transition-colors font-['Montserrat']"
                    />
                    <input
                      name="email"
                      type="email"
                      required
                      value={leadEmail}
                      onChange={(e) => setLeadEmail(e.target.value)}
                      placeholder="Seu melhor e-mail profissional"
                      className="w-full px-4 py-3.5 bg-white/8 border border-white/15 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-gold-400/60 transition-colors font-['Montserrat']"
                    />
                    <button
                      type="submit"
                      disabled={leadLoading}
                      className="w-full py-4 bg-gradient-to-r from-gold-500 to-gold-700 text-charcoal-900 font-bold rounded-xl hover:from-gold-600 hover:to-gold-800 transition-all text-sm tracking-wide font-['Montserrat'] disabled:opacity-60 cursor-pointer whitespace-nowrap"
                    >
                      {leadLoading ? 'Enviando...' : 'Quero meu protocolo personalizado'}
                    </button>
                  </form>

                  <p className="text-white/20 text-xs mt-4 font-['Montserrat']">
                    Seus dados são confidenciais · Sem spam · Cancele quando quiser
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-gradient-to-br from-charcoal-900 to-charcoal-950 rounded-2xl p-10 border border-gold-500/10 text-center">
                <div className="w-14 h-14 rounded-full bg-gold-500/15 border border-gold-500/25 flex items-center justify-center mx-auto mb-5">
                  <i className="ri-checkbox-circle-fill text-3xl text-gold-400"></i>
                </div>
                <h3 className="font-display text-2xl font-bold text-white mb-3">Protocolo enviado!</h3>
                <p className="text-white/50 text-sm font-['Montserrat'] font-light leading-relaxed mb-7">
                  Verifique seu e-mail. Enquanto isso, pratique agora uma das técnicas recomendadas:
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  {result.tecnicas.map((t) => (
                    <Link
                      key={t.slug}
                      to={`/exercicio-gratis/${t.slug}`}
                      className="px-6 py-3 bg-gold-500/10 border border-gold-500/20 text-gold-400 rounded-full text-sm font-semibold hover:bg-gold-500/20 transition-all whitespace-nowrap font-['Montserrat']"
                    >
                      {t.nome}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Refazer */}
            <div className="text-center mt-8">
              <button
                onClick={() => { setStep('intro'); setCurrentQ(0); setAnswers([]); setResult(null); setLeadSent(false); }}
                className="text-charcoal-400 hover:text-charcoal-600 text-sm font-['Montserrat'] transition-colors cursor-pointer"
              >
                <i className="ri-refresh-line mr-1.5"></i>
                Refazer o diagnóstico
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
